import React, { useEffect, useRef } from "react";

/**
 * Cinematic video interlude — pure atmosphere between sections.
 * No UI, no overlays, no copy. Autoplay, loop, muted, playsInline.
 * Dual <source> (WebM/VP9 primary, MP4/H.264 fallback) for universal browser support.
 * Subtle fade gradients at top + bottom blend the video into the dark surrounding sections.
 */
export default function CinematicVideo({
  webm = "/videos/sections/ag_cinematic.webm",
  mp4 = "/videos/sections/ag_cinematic.mp4",
  height = "h-[70vh] md:h-[85vh] lg:h-screen",
  testid = "cinematic-video",
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.then === "function") p.catch(() => {});
    };
    tryPlay();
    v.addEventListener("canplay", tryPlay, { once: true });
    return () => v.removeEventListener("canplay", tryPlay);
  }, []);

  return (
    <section
      data-testid={testid}
      className={`relative w-full ${height} overflow-hidden bg-midnight`}
      aria-hidden="true"
    >
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload noplaybackrate nofullscreen"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src={webm} type='video/webm; codecs="vp9"' />
        <source src={mp4} type='video/mp4; codecs="avc1.42E01E"' />
      </video>

      {/* Top fade — blends with The Distinction */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-32 lg:h-56 z-10"
        style={{ background: "linear-gradient(180deg, rgb(10,17,40) 0%, rgba(10,17,40,0.7) 35%, transparent 100%)" }}
      />

      {/* Bottom fade — blends into next section */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-32 lg:h-56 z-10"
        style={{ background: "linear-gradient(0deg, rgb(10,17,40) 0%, rgba(10,17,40,0.7) 35%, transparent 100%)" }}
      />
    </section>
  );
}
